import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Eye, Download, FolderPlus, Pencil, Trash2, X, Check } from "lucide-react";
import Shell from "@/components/Shell";
import { Card, PageHeader, Btn, Input, Spinner, Empty, StatusBadge } from "@/components/ui-kit";
import { formatGBP, formatDateUK, STATUS_DOT, STATUS_BG, QUOTE_STATUSES } from "@/lib/format";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { useConfirm } from "@/lib/confirm";
import { downloadFromUrl } from "@/lib/download";

const buildPdfHref = (quoteId) => {
  let token = "";
  try { token = localStorage.getItem("kerboom_token") || ""; } catch { /* ignored */ }
  return `${API}/quotes/${quoteId}/pdf${token ? `?token=${encodeURIComponent(token)}` : ""}`;
};

const isAccepted = (q) => ["Client Selected", "Accepted"].includes(q.status);
const isUnseen = (q) => !!q.client_selected_at && (q.client_seen_at || "") < q.client_selected_at;

const AcceptedBadge = ({ q }) => (
  <div className="flex items-center gap-2 mb-3" data-testid={`quote-accepted-badge-${q.id}`}>
    <span className="relative inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-full pl-1 pr-3 py-1 text-xs font-semibold shadow-sm">
      <span className="w-5 h-5 rounded-full bg-white/20 grid place-items-center"><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>
      Quote Accepted
    </span>
    {isUnseen(q) && (
      <span className="inline-flex items-center gap-1 bg-red-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider animate-pulse" data-testid={`quote-new-badge-${q.id}`}>
        New
      </span>
    )}
  </div>
);

const QuoteCard = ({ q, onDelete, onDownload, onMakeProject }) => (
  <Card data-testid={`quote-card-${q.id}`} className={`p-5 hover:shadow-sm transition-all ${STATUS_BG[q.status] || ""} ${isAccepted(q) ? "ring-2 ring-emerald-500/60" : ""}`}>
    {isAccepted(q) && <AcceptedBadge q={q} />}
    <div className="flex items-start justify-between gap-2 mb-2">
      <Link to={`/quotes/${q.id}`} className="flex-1 min-w-0">
        <p className="font-display text-lg font-semibold text-zinc-950 leading-snug hover:text-zinc-700 transition-colors">{q.name}</p>
      </Link>
      <StatusBadge status={q.status} />
    </div>
    <p className="text-xs text-zinc-400 font-mono mb-3">Q{q.quote_number}</p>
    <div className="space-y-1 text-sm text-zinc-600">
      <p><span className="text-zinc-500">Client:</span> {q.client_name}</p>
      {q.linked_project && (
        <p><span className="text-zinc-500">Project:</span> <Link to={`/projects/${q.linked_project.id}`} className="text-zinc-900 hover:underline">{q.linked_project.name}</Link></p>
      )}
      <p><span className="text-zinc-500">Date:</span> {formatDateUK(q.date)}</p>
    </div>
    <p className="font-display text-2xl font-bold text-zinc-950 mt-4 mb-0.5">{formatGBP(q.total)}</p>
    <p className="text-[11px] text-zinc-500 uppercase tracking-wider">Total Amount</p>
    <div className="flex items-center flex-wrap gap-1.5 mt-4">
      <Link to={`/quotes/${q.id}`}>
        <Btn size="sm" data-testid={`quote-view-${q.id}`}><Eye className="w-3.5 h-3.5" />View</Btn>
      </Link>
      <Btn as="a" size="sm" variant="secondary" href={buildPdfHref(q.id)} target="_blank" rel="noopener" data-testid={`quote-pdf-${q.id}`}><Download className="w-3.5 h-3.5" />PDF</Btn>
      {!q.linked_project_id && (
        <Btn size="sm" variant="secondary" onClick={() => onMakeProject(q)} data-testid={`quote-make-project-${q.id}`}><FolderPlus className="w-3.5 h-3.5" />Make Project</Btn>
      )}
      <Link to={`/quotes/${q.id}/edit`}>
        <Btn size="sm" variant="secondary" data-testid={`quote-edit-${q.id}`}><Pencil className="w-3.5 h-3.5" />Edit</Btn>
      </Link>
      <button onClick={() => onDelete(q.id)} className="p-1.5 text-white bg-red-500 hover:bg-red-600 rounded-full" data-testid={`quote-delete-${q.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  </Card>
);

const QuotesList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const clientFilter = searchParams.get("client") || "";
  const liveFilter = searchParams.get("filter") === "live";
  const [clients, setClients] = useState([]);
  const navigate = useNavigate();
  const confirm = useConfirm();

  const clientName = clients.find((c) => c.id === clientFilter)?.name;

  const clearClientFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("client");
    next.delete("filter");
    setSearchParams(next);
  };

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/quotes");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

  const del = async (id) => {
    if (!(await confirm({ title: "Delete this quote?", description: "This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/quotes/${id}`);
    await load();
  };

  const downloadPdf = (quote) => {
    downloadFromUrl(`/quotes/${quote.id}/pdf`);
  };

  const makeProject = async (quote) => {
    if (!(await confirm({ title: `Create a project from "${quote.name}"?`, description: "The tasks will be copied into a new project.", confirmText: "Create Project" }))) return;
    const { data } = await api.post(`/quotes/${quote.id}/make-project`);
    navigate(`/projects/${data.id}`);
  };

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    const numericNeedle = needle.replace(/^q/, ""); // allow "Q8083" or "8083"
    return items.filter((x) => {
      if (clientFilter && x.client_id !== clientFilter) return false;
      if (liveFilter && !["Draft", "Sent"].includes(x.status)) return false;
      if (!needle) return true;
      return (
        (x.name || "").toLowerCase().includes(needle) ||
        (x.client_name || "").toLowerCase().includes(needle) ||
        String(x.quote_number).includes(numericNeedle)
      );
    });
  }, [items, q, clientFilter, liveFilter]);

  const byStatus = useMemo(() => {
    const map = Object.fromEntries(QUOTE_STATUSES.map((s) => [s, []]));
    filtered.forEach((x) => { if (map[x.status]) map[x.status].push(x); });
    return map;
  }, [filtered]);

  return (
    <Shell>
      <PageHeader
        testId="quotes-header"
        title="Quotes"
        subtitle="Manage your project quotes"
        actions={
          <Btn onClick={() => navigate("/quotes/new")} data-testid="new-quote-btn">
            <Plus className="w-4 h-4" />New Quote
          </Btn>
        }
      />

      <div className="mb-6 max-w-md">
        <Input
          data-testid="quotes-search"
          placeholder="Search by quote name, client or Q-number..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {clientFilter && clientName && (
        <div className="mb-4 inline-flex items-center gap-2 bg-zinc-950 text-white rounded-full pl-3 pr-1.5 py-1 text-xs font-medium" data-testid="quotes-filter-chip">
          <span>{clientName}{liveFilter ? " · live" : ""}</span>
          <button
            onClick={clearClientFilter}
            className="p-0.5 hover:bg-white/20 rounded-full transition-colors ml-1"
            data-testid="quotes-filter-clear"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}


      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : items.length === 0 ? (
        <Empty
          title="No quotes yet"
          description="Create your first quote to start winning work."
          action={<Btn onClick={() => navigate("/quotes/new")}><Plus className="w-4 h-4" />New Quote</Btn>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {QUOTE_STATUSES.map((status) => (
            <div key={status} data-testid={`quotes-col-${status.toLowerCase()}`} className="flex flex-col">
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status]}`} />
                  <p className="text-sm font-semibold text-zinc-950">{status}</p>
                </div>
                <span className="text-xs text-zinc-500 bg-zinc-100 rounded-full px-2 py-0.5">{byStatus[status].length}</span>
              </div>
              <div className="space-y-3 min-h-[80px]">
                {byStatus[status].length === 0 ? (
                  <p className="text-xs text-zinc-400 italic text-center py-4">No quotes</p>
                ) : byStatus[status].map((x) => (
                  <QuoteCard key={x.id} q={x} onDelete={del} onDownload={downloadPdf} onMakeProject={makeProject} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
};

export default QuotesList;
